// src/pages/projects/ClientsPage.jsx
// Clients management page
// BRIO Migration: Listing table code "1a", Modal form table code "1b"
// Client Detail View modal with Overview + People + Notes tabs
// ============================================================================
// UPDATE:
//   (#2) Clients created via Inquiries have origin='inquiry' and isConverted=false.
//   They are excluded from the active list until conversion sets isConverted=true.
//   Manual clients (origin='manual') always appear. Enforced via filteredActiveClients.
// ============================================================================
import React, { useState, useMemo, useCallback } from 'react';
import { Plus, ChevronDown, ChevronUp, Trash2, UserPlus, Minus } from 'lucide-react';
import {
  BrioAnalyticsSurface,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioActionGroup,
  BrioFrame,
  BrioModal,
  BrioDatePicker,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { TAX_REGIME_OPTIONS, normalizeTaxRegime } from '../../utils/invoiceTaxRegime';


// Presentation codes
const PRESENTATION_CODE = '1a';
const MODAL_TABLE_CODE = '1b';

// ── Shared input styles (matches TeamMembersPage / ManageProjectsPage) ──
const INPUT_STYLE = {
  width: '100%',
  padding: '6px 10px',
  border: '1px solid var(--brio-border, #d1d5db)',
  borderRadius: '6px',
  fontSize: '13px',
  lineHeight: '1.4',
  background: '#fff',
  color: 'var(--brio-text, #1e293b)',
  boxSizing: 'border-box',
};
const SELECT_STYLE = { ...INPUT_STYLE, appearance: 'auto' };
const TEXTAREA_STYLE = { ...INPUT_STYLE, minHeight: '56px', resize: 'vertical' };
const HINT_STYLE = {
  fontSize: '11px',
  color: 'var(--brio-muted, #94a3b8)',
  marginTop: '2px',
  lineHeight: '1.3',
};

// ── Dietary options ─────────────────────────────────────────────────
const DIETARY_OPTIONS = [
  { value: '', label: '— None —' },
  { value: 'veg', label: 'Vegetarian' },
  { value: 'non-veg', label: 'Non-vegetarian' },
  { value: 'eggetarian', label: 'Eggetarian' },
  { value: 'vegan', label: 'Vegan' },
];

// ── Relationship types for additional contacts ──────────────────────
const RELATIONSHIP_TYPES = [
  { value: '', label: '— Select —' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'husband', label: 'Husband' },
  { value: 'wife', label: 'Wife' },
  { value: 'pa', label: 'PA / Assistant' },
  { value: 'partner', label: 'Business Partner' },
  { value: 'family', label: 'Family Member' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'other', label: 'Other…' },
];

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ── Year-unknown sentinel: year 1900 means "year not known" ────────
const YEAR_UNKNOWN = '1900';
const isYearUnknown = (dateStr) => Boolean(dateStr && dateStr.startsWith(YEAR_UNKNOWN + '-'));

// ── Blank contact template ──────────────────────────────────────────
const blankContact = () => ({
  name: '',
  relationship: '',
  relationshipOther: '',
  organisation: '',
  company: '',
  linkedinUrl: '',
  instagramUrl: '',
  address: '',
  mobile: '',
  whatsapp: '',
  customPhoneLabel: '',
  customPhoneNumber: '',
  primaryEmail: '',
  secondaryEmail: '',
  dob: '',
  anniversary: '',
  customDateLabel: '',
  customDate: '',
  dietary: '',
  dietaryOther: '',
  isPrimary: false,
});

// ── Blank form state ────────────────────────────────────────────────
const blankClientForm = () => ({
  // Client identity (Overview tab)
  clientName: '',
  clientOrganisation: '',
  // Primary contact relationship (People tab)
  clientRelationship: '',
  clientRelationshipOther: '',
  // Primary contact communication (People tab)
  clientCompany: '',
  clientLinkedinUrl: '',
  clientInstagramUrl: '',
  clientAddress: '',
  clientMobile: '',
  clientWhatsapp: '',
  clientCustomPhoneLabel: '',
  clientCustomPhoneNumber: '',
  clientPrimaryEmail: '',
  clientSecondaryEmail: '',
  // Primary contact dates
  clientDob: '',
  clientAnniversary: '',
  clientCustomDateLabel: '',
  clientCustomDate: '',
  // Primary contact preferences
  clientDietary: '',
  clientDietaryOther: '',
  // Additional contacts (array)
  additionalContacts: [],
  // Invoicing (Overview tab)
  invoicingName: '',
  invoiceType: '',
  invoiceAddress: '',
  gst: '',
  pan: '',
  sacCode: '',
  placeOfSupply: '',
  vatBtw: '',
  paymentTermsDays: '',
  defaultTaxRegime: '',
  requiresProformaInvoice: null,
  plannedDateTriggerThresholdDays: '',
  // Notes tab
  likesDislikes: '',
});

// ═══════════════════════════════════════════════════════════════════
// FormRow: label/input table-row component (BRIO 1b)
// ═══════════════════════════════════════════════════════════════════
function FormRow({ label, hint, children, sectionHeader }) {
  if (sectionHeader) {
    return (
      <tr>
        <td colSpan="2" style={{
          padding: '10px 12px 6px',
          fontWeight: 600,
          fontSize: '12px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--brio-gold, #9F902A)',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: 'var(--brio-bg-subtle, #fafaf6)',
        }}>
          {label}
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={{
        padding: '8px 12px',
        verticalAlign: 'top',
        whiteSpace: 'nowrap',
        fontWeight: 500,
        fontSize: '13px',
        color: 'var(--brio-text, #475569)',
        width: '155px',
        minWidth: '120px',
      }}>
        {label}
        {hint && <div style={HINT_STYLE}>{hint}</div>}
      </td>
      <td style={{ padding: '6px 12px', verticalAlign: 'top' }}>
        {children}
      </td>
    </tr>
  );
}

// ═══════════════════════════════════════════════════════════════════
// FlexibleDateInput: date input with year-unknown toggle
// Uses year 1900 as sentinel for "year unknown"
// ═══════════════════════════════════════════════════════════════════
function FlexibleDateInput({ value, onChange, showAge }) {
  const yearUnknown = isYearUnknown(value);
  const parts = (value || '').split('-');
  const month = parts.length >= 2 ? parts[1] : '';
  const day = parts.length >= 3 ? parts[2] : '';

  // Calculate age (only when year is known)
  let ageStr = null;
  if (showAge && value && !yearUnknown) {
    const y = parseInt(parts[0]);
    if (!isNaN(y) && y > 1900) {
      const today = new Date();
      let age = today.getFullYear() - y;
      const mDiff = (today.getMonth() + 1) - parseInt(month);
      if (mDiff < 0 || (mDiff === 0 && today.getDate() < parseInt(day))) age--;
      if (age >= 0) ageStr = `${age} yrs`;
    }
  }

  const handleToggle = () => {
    if (yearUnknown) {
      // Switch to year-known: use current year if month/day exist
      if (month && day) {
        onChange(`${new Date().getFullYear()}-${month}-${day}`);
      } else {
        onChange('');
      }
    } else {
      // Switch to year-unknown
      if (value && parts.length === 3) {
        onChange(`${YEAR_UNKNOWN}-${month}-${day}`);
      } else {
        onChange(`${YEAR_UNKNOWN}-01-01`);
      }
    }
  };

  const cbStyle = { fontSize: '11px', color: 'var(--brio-muted, #94a3b8)', display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'pointer', whiteSpace: 'nowrap' };

  if (yearUnknown) {
    return (
      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={day}
          onChange={e => onChange(`${YEAR_UNKNOWN}-${month || '01'}-${e.target.value}`)}
          style={{ ...SELECT_STYLE, width: 'auto', maxWidth: '70px' }}
        >
          <option value="">Day</option>
          {Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, '0')).map(d =>
            <option key={d} value={d}>{parseInt(d)}</option>
          )}
        </select>
        <select
          value={month}
          onChange={e => onChange(`${YEAR_UNKNOWN}-${e.target.value}-${day || '01'}`)}
          style={{ ...SELECT_STYLE, width: 'auto', maxWidth: '90px' }}
        >
          <option value="">Month</option>
          {MONTH_NAMES.map((m, i) =>
            <option key={i} value={String(i + 1).padStart(2, '0')}>{m}</option>
          )}
        </select>
        <label style={cbStyle}>
          <input type="checkbox" checked onChange={handleToggle} style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
          Year unknown
        </label>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
      <BrioDatePicker
        value={value || ''}
        onChange={v => onChange(v)}
        clearable={false}
        placeholder="Select date"
      />
      {ageStr && <span style={{ fontSize: '11px', color: 'var(--brio-gold, #9F902A)', fontWeight: 500 }}>({ageStr})</span>}
      <label style={cbStyle}>
        <input type="checkbox" checked={false} onChange={handleToggle} style={{ accentColor: 'var(--brio-gold, #9F902A)' }} />
        Year unknown
      </label>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ContactFieldsSection: renders contact fields with expand/collapse
// expandMode: 'auto' (show filled only) | 'expanded' (show all)
// hideIdentity: omit Name/Organisation (for primary contact since
//               those fields are in the Overview tab)
// ═══════════════════════════════════════════════════════════════════
function ContactFieldsSection({ data, onChange, showRelationship, hideIdentity, expandMode, onExpandMode }) {
  const set = (field, value) => onChange({ ...data, [field]: value });

  const hasVal = useCallback((key) => {
    const v = data[key];
    return v !== undefined && v !== null && v !== '';
  }, [data]);

  const show = useCallback((key) => {
    if (expandMode === 'expanded') return true;
    return hasVal(key);
  }, [expandMode, hasVal]);

  const showComposite = useCallback((...keys) => {
    if (expandMode === 'expanded') return true;
    return keys.some(k => hasVal(k));
  }, [expandMode, hasVal]);

  // Count hidden fields for "N more fields" display
  const fieldChecks = [
    ...(hideIdentity ? [] : [['organisation']]),
    ['company'],
    ['linkedinUrl'], ['instagramUrl'], ['address'],
    ['mobile'], ['whatsapp'],
    ['customPhoneLabel', 'customPhoneNumber'],
    ['primaryEmail'], ['secondaryEmail'],
    ['dob'], ['anniversary'],
    ['customDateLabel', 'customDate'],
    ['dietary'], ['dietaryOther'],
  ];
  const hiddenCount = expandMode === 'expanded' ? 0 : fieldChecks.reduce((count, keys) => {
    return count + (keys.some(k => hasVal(k)) ? 0 : 1);
  }, 0);

  return (
    <>
      {/* ── Identity (additional contacts only) ── */}
      {showRelationship && (
        <FormRow label="Relationship">
          <div style={{ display: 'flex', gap: '8px' }}>
            <select value={data.relationship || ''} onChange={e => set('relationship', e.target.value)} style={{ ...SELECT_STYLE, maxWidth: '170px' }}>
              {RELATIONSHIP_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {data.relationship === 'other' && (
              <input type="text" value={data.relationshipOther || ''} onChange={e => set('relationshipOther', e.target.value)} placeholder="Specify…" style={{ ...INPUT_STYLE, maxWidth: '180px' }} />
            )}
          </div>
        </FormRow>
      )}
      {!hideIdentity && (
        <FormRow label="Name">
          <input type="text" value={data.name || ''} onChange={e => set('name', e.target.value)} placeholder="Full name" style={INPUT_STYLE} />
        </FormRow>
      )}
      {!hideIdentity && show('organisation') && (
        <FormRow label="Organisation">
          <input type="text" value={data.organisation || ''} onChange={e => set('organisation', e.target.value)} placeholder="Company / Org" style={INPUT_STYLE} />
        </FormRow>
      )}

      {/* ── Company (where this person works) ── */}
      {show('company') && (
        <FormRow label="Company" hint="Where they work">
          <input type="text" value={data.company || ''} onChange={e => set('company', e.target.value)} placeholder="Employer / company name" style={INPUT_STYLE} />
        </FormRow>
      )}

      {/* ── Communication ── */}
      {show('linkedinUrl') && (
        <FormRow label="LinkedIn">
          <input type="url" value={data.linkedinUrl || ''} onChange={e => set('linkedinUrl', e.target.value)} placeholder="https://linkedin.com/in/…" style={INPUT_STYLE} />
        </FormRow>
      )}
      {show('instagramUrl') && (
        <FormRow label="Instagram">
          <input type="url" value={data.instagramUrl || ''} onChange={e => set('instagramUrl', e.target.value)} placeholder="https://instagram.com/…" style={INPUT_STYLE} />
        </FormRow>
      )}
      {show('address') && (
        <FormRow label="Address">
          <textarea value={data.address || ''} onChange={e => set('address', e.target.value)} placeholder="Street, city, country…" style={TEXTAREA_STYLE} rows={2} />
        </FormRow>
      )}
      {show('mobile') && (
        <FormRow label="Mobile">
          <input type="tel" value={data.mobile || ''} onChange={e => set('mobile', e.target.value)} placeholder="+91 …" style={{ ...INPUT_STYLE, maxWidth: '220px' }} />
        </FormRow>
      )}
      {show('whatsapp') && (
        <FormRow label="WhatsApp">
          <input type="tel" value={data.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} placeholder="+91 …" style={{ ...INPUT_STYLE, maxWidth: '220px' }} />
        </FormRow>
      )}
      {showComposite('customPhoneLabel', 'customPhoneNumber') && (
        <FormRow label="Custom Phone" hint="Label + number">
          <div style={{ display: 'flex', gap: '8px' }}>
            <input type="text" value={data.customPhoneLabel || ''} onChange={e => set('customPhoneLabel', e.target.value)} placeholder="Office" style={{ ...INPUT_STYLE, maxWidth: '100px' }} />
            <input type="tel" value={data.customPhoneNumber || ''} onChange={e => set('customPhoneNumber', e.target.value)} placeholder="+91 …" style={{ ...INPUT_STYLE, flex: 1 }} />
          </div>
        </FormRow>
      )}
      {show('primaryEmail') && (
        <FormRow label="Email">
          <input type="email" value={data.primaryEmail || ''} onChange={e => set('primaryEmail', e.target.value)} placeholder="Primary email" style={INPUT_STYLE} />
        </FormRow>
      )}
      {show('secondaryEmail') && (
        <FormRow label="Email 2">
          <input type="email" value={data.secondaryEmail || ''} onChange={e => set('secondaryEmail', e.target.value)} placeholder="Secondary (optional)" style={INPUT_STYLE} />
        </FormRow>
      )}

      {/* ── Important Dates ── */}
      {show('dob') && (
        <FormRow label="Date of Birth">
          <FlexibleDateInput value={data.dob || ''} onChange={v => set('dob', v)} showAge />
        </FormRow>
      )}
      {show('anniversary') && (
        <FormRow label="Anniversary">
          <FlexibleDateInput value={data.anniversary || ''} onChange={v => set('anniversary', v)} />
        </FormRow>
      )}
      {showComposite('customDateLabel', 'customDate') && (
        <FormRow label="Custom Date" hint="Label + date">
          <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
            <input type="text" value={data.customDateLabel || ''} onChange={e => set('customDateLabel', e.target.value)} placeholder="e.g. Housewarming, Pet birthday…" style={{ ...INPUT_STYLE, maxWidth: '280px' }} />
            <FlexibleDateInput value={data.customDate || ''} onChange={v => set('customDate', v)} />
          </div>
        </FormRow>
      )}

      {/* ── Preferences ── */}
      {show('dietary') && (
        <FormRow label="Dietary">
          <select value={data.dietary || ''} onChange={e => set('dietary', e.target.value)} style={{ ...SELECT_STYLE, maxWidth: '200px' }}>
            {DIETARY_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </FormRow>
      )}
      {show('dietaryOther') && (
        <FormRow label="Others" hint="Preferences / specifics">
          <textarea
            value={data.dietaryOther || ''}
            onChange={e => set('dietaryOther', e.target.value)}
            placeholder="Allergies, preferences, notes…"
            style={{ ...TEXTAREA_STYLE, minHeight: '40px' }}
            rows={2}
          />
        </FormRow>
      )}

      {/* ── Communication Preferences (shown when email exists) ── */}
      {(data.primaryEmail || data.email) && (expandMode === 'expanded' || data.useForInvoicing || data.useForApprovals || data.useForStageUpdates) && (
        <FormRow label="Email for…" hint="Auto-suggest on timeline events">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {[
              { key: 'useForInvoicing', label: 'Invoicing' },
              { key: 'useForApprovals', label: 'Approvals' },
              { key: 'useForStageUpdates', label: 'Stage updates' },
            ].map(flag => (
              <label key={flag.key} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!data[flag.key]}
                  onChange={(e) => set(flag.key, e.target.checked)}
                  style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                />
                {flag.label}
              </label>
            ))}
          </div>
        </FormRow>
      )}

      {/* ── Expand / Collapse toggle ── */}
      <tr>
        <td colSpan="2" style={{ padding: '4px 12px 8px', textAlign: 'center' }}>
          {expandMode !== 'expanded' && hiddenCount > 0 && (
            <button
              type="button"
              onClick={() => onExpandMode('expanded')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: 500, color: 'var(--brio-gold, #9F902A)',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <Plus size={12} /> {hiddenCount} more field{hiddenCount !== 1 ? 's' : ''}
            </button>
          )}
          {expandMode === 'expanded' && (
            <button
              type="button"
              onClick={() => onExpandMode('auto')}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted, #94a3b8)',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <Minus size={12} /> Show less
            </button>
          )}
        </td>
      </tr>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Tab + Button group styles
// ═══════════════════════════════════════════════════════════════════
const TAB_STYLE = {
  padding: '8px 20px',
  fontSize: '13px',
  fontWeight: 500,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  borderBottom: '2px solid transparent',
  color: 'var(--brio-muted, #94a3b8)',
  transition: 'color 0.15s, border-color 0.15s',
};
const TAB_ACTIVE_STYLE = {
  ...TAB_STYLE,
  color: 'var(--brio-gold, #9F902A)',
  borderBottomColor: 'var(--brio-gold, #9F902A)',
};

const TYPE_BTN_BASE = {
  padding: '6px 18px',
  fontSize: '13px',
  fontWeight: 500,
  border: '1px solid var(--brio-border, #d1d5db)',
  cursor: 'pointer',
  transition: 'all 0.15s',
};
const TYPE_BTN_ACTIVE = {
  ...TYPE_BTN_BASE,
  background: 'var(--brio-gold, #9F902A)',
  color: '#fff',
  borderColor: 'var(--brio-gold, #9F902A)',
};
const TYPE_BTN_INACTIVE = {
  ...TYPE_BTN_BASE,
  background: '#fff',
  color: 'var(--brio-text, #475569)',
};

// Primary badge style
/* @audit cat="03" issue="borderRadius 10px — not a DS token (0/4/8)" fix="Use BrioBadge or borderRadius 8px" */
const PRIMARY_BADGE = {
  fontSize: '10px',
  padding: '1px 8px',
  borderRadius: '10px',
  fontWeight: 500,
  lineHeight: '1.6',
  whiteSpace: 'nowrap',
};
const PRIMARY_BADGE_ON = {
  ...PRIMARY_BADGE,
  background: 'var(--brio-gold, #9F902A)',
  color: '#fff',
  border: '1px solid var(--brio-gold, #9F902A)',
  cursor: 'pointer',
};
const PRIMARY_BADGE_OFF = {
  ...PRIMARY_BADGE,
  background: 'transparent',
  color: 'var(--brio-muted, #94a3b8)',
  border: '1px solid var(--brio-border, #d1d5db)',
  cursor: 'pointer',
};
const PRIMARY_BADGE_FIXED = {
  ...PRIMARY_BADGE,
  background: 'var(--brio-gold, #9F902A)',
  color: '#fff',
  border: '1px solid var(--brio-gold, #9F902A)',
  cursor: 'default',
};

// ═══════════════════════════════════════════════════════════════════
// ClientDetailViewBody: tabbed body (Overview + People + Notes)
// ═══════════════════════════════════════════════════════════════════
function ClientDetailViewBody({ form, setForm }) {
  const [activeTab, setActiveTab] = useState('overview');
  const modalTableProps = getTablePropsFromCode(MODAL_TABLE_CODE);

  const setPrimary = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // ── People tab state ─────────────────────────────────────────────
  // Drawer open/close: true = open, false = closed
  const [openDrawers, setOpenDrawers] = useState({ primary: true });
  // Field expand mode per contact: 'auto' | 'expanded'
  const [expandModes, setExpandModes] = useState({});

  const isDrawerOpen = (key) => openDrawers[key] ?? (key === 'primary');
  const toggleDrawer = (key) => setOpenDrawers(p => ({ ...p, [key]: !(p[key] ?? (key === 'primary')) }));
  const getExpandMode = (key) => expandModes[key] || 'auto';
  const setExpandMode = (key, mode) => setExpandModes(p => ({ ...p, [key]: mode }));

  const contacts = form.additionalContacts || [];
  const setContacts = (newContacts) => setForm(f => ({ ...f, additionalContacts: newContacts }));
  const addContact = () => {
    const newIdx = contacts.length;
    setContacts([...contacts, blankContact()]);
    setOpenDrawers(p => ({ ...p, [`contact-${newIdx}`]: true }));
    setExpandModes(p => ({ ...p, [`contact-${newIdx}`]: 'expanded' }));
  };
  const removeContact = (idx) => setContacts(contacts.filter((_, i) => i !== idx));
  const updateContact = (idx, updated) => setContacts(contacts.map((c, i) => i === idx ? updated : c));
  const togglePrimary = (idx) => setContacts(contacts.map((c, i) => i === idx ? { ...c, isPrimary: !c.isPrimary } : c));

  const collapseAll = () => {
    const closed = { primary: false };
    contacts.forEach((_, i) => { closed[`contact-${i}`] = false; });
    setOpenDrawers(closed);
    setExpandModes({});
  };

  // -- primary contact as a data object for ContactFieldsSection
  const primaryData = {
    name: form.clientName,
    organisation: form.clientOrganisation,
    relationship: form.clientRelationship,
    relationshipOther: form.clientRelationshipOther,
    company: form.clientCompany,
    linkedinUrl: form.clientLinkedinUrl,
    instagramUrl: form.clientInstagramUrl,
    address: form.clientAddress,
    mobile: form.clientMobile,
    whatsapp: form.clientWhatsapp,
    customPhoneLabel: form.clientCustomPhoneLabel,
    customPhoneNumber: form.clientCustomPhoneNumber,
    primaryEmail: form.clientPrimaryEmail,
    secondaryEmail: form.clientSecondaryEmail,
    dob: form.clientDob,
    anniversary: form.clientAnniversary,
    customDateLabel: form.clientCustomDateLabel,
    customDate: form.clientCustomDate,
    dietary: form.clientDietary,
    dietaryOther: form.clientDietaryOther,
  };
  const onPrimaryChange = (updated) => {
    setForm(f => ({
      ...f,
      clientName: updated.name,
      clientOrganisation: updated.organisation,
      clientRelationship: updated.relationship,
      clientRelationshipOther: updated.relationshipOther,
      clientCompany: updated.company,
      clientLinkedinUrl: updated.linkedinUrl,
      clientInstagramUrl: updated.instagramUrl,
      clientAddress: updated.address,
      clientMobile: updated.mobile,
      clientWhatsapp: updated.whatsapp,
      clientCustomPhoneLabel: updated.customPhoneLabel,
      clientCustomPhoneNumber: updated.customPhoneNumber,
      clientPrimaryEmail: updated.primaryEmail,
      clientSecondaryEmail: updated.secondaryEmail,
      clientDob: updated.dob,
      clientAnniversary: updated.anniversary,
      clientCustomDateLabel: updated.customDateLabel,
      clientCustomDate: updated.customDate,
      clientDietary: updated.dietary,
      clientDietaryOther: updated.dietaryOther,
    }));
  };

  // Drawer header row renderer
  const renderDrawerHeader = (key, label, extras) => {
    const open = isDrawerOpen(key);
    return (
      <tr>
        <td colSpan="2" style={{
          padding: '6px 12px',
          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
          background: '#fafbfc',
          cursor: 'pointer',
          userSelect: 'none',
        }}
          onClick={() => toggleDrawer(key)}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 500 }}>
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              <span>{label}</span>
              {extras}
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* ── Tab bar ────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--brio-border, #e2e8f0)',
        marginBottom: '4px',
      }}>
        {['overview', 'people', 'notes'].map(tab => (
          <button key={tab} type="button" onClick={() => setActiveTab(tab)} style={activeTab === tab ? TAB_ACTIVE_STYLE : TAB_STYLE}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════
          OVERVIEW TAB
         ═══════════════════════════════════════════════════ */}
      {activeTab === 'overview' && (
        <BrioByCode code={MODAL_TABLE_CODE}>
          <BrioTable {...modalTableProps}>
            <tbody>

              <FormRow sectionHeader label="Client Information" />

              <FormRow label="Name" hint="Contact person's full name">
                <input type="text" value={form.clientName || ''} onChange={e => setPrimary('clientName', e.target.value)} placeholder="Full name" style={INPUT_STYLE} />
              </FormRow>

              <FormRow label="Organisation" hint="Company / family name">
                <input type="text" value={form.clientOrganisation || ''} onChange={e => setPrimary('clientOrganisation', e.target.value)} placeholder="Company / Organisation" style={INPUT_STYLE} />
              </FormRow>

              <FormRow sectionHeader label="Invoicing" />

              <FormRow label="Invoicing Name" hint="Name shown on invoices — defaults to client name if left blank">
                <input
                  type="text"
                  value={form.invoicingName || ''}
                  onChange={e => setPrimary('invoicingName', e.target.value)}
                  placeholder={form.clientOrganisation || form.clientName || 'Defaults to client name'}
                  style={INPUT_STYLE}
                />
              </FormRow>

              <FormRow label="Invoice Type" hint="B2B or B2C classification">
                <div style={{ display: 'flex' }}>
                  {['B2B', 'B2C'].map((type, i) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setPrimary('invoiceType', form.invoiceType === type ? '' : type)}
                      style={{
                        ...(form.invoiceType === type ? TYPE_BTN_ACTIVE : TYPE_BTN_INACTIVE),
                        borderRadius: i === 0 ? '6px 0 0 6px' : '0 6px 6px 0',
                        ...(i > 0 ? { borderLeft: 'none' } : {}),
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </FormRow>

              <FormRow label="Invoice Address" hint="Billing address for invoices">
                <textarea value={form.invoiceAddress || ''} onChange={e => setPrimary('invoiceAddress', e.target.value)} placeholder="Street, city, state, PIN…" style={TEXTAREA_STYLE} rows={3} />
              </FormRow>

              {form.invoiceType !== 'B2C' && (
                <>
                  <FormRow label="GST" hint="GST number">
                    <input type="text" value={form.gst || ''} onChange={e => setPrimary('gst', e.target.value.toUpperCase())} placeholder="e.g. 27AABCU9603R1ZM" style={{ ...INPUT_STYLE, maxWidth: '300px' }} />
                  </FormRow>
                  <FormRow label="PAN" hint="PAN number">
                    <input type="text" value={form.pan || ''} onChange={e => setPrimary('pan', e.target.value.toUpperCase())} placeholder="e.g. AABCU9603R" style={{ ...INPUT_STYLE, maxWidth: '240px' }} />
                  </FormRow>
                </>
              )}

              <FormRow label="SAC Code" hint="Service Accounting Code for GST">
                <input type="text" value={form.sacCode || ''} onChange={e => setPrimary('sacCode', e.target.value)} placeholder="e.g. 998332" style={{ ...INPUT_STYLE, maxWidth: '200px' }} />
              </FormRow>

              <FormRow label="Place of Supply" hint="State / UT for GST purposes">
                <input type="text" value={form.placeOfSupply || ''} onChange={e => setPrimary('placeOfSupply', e.target.value)} placeholder="e.g. Maharashtra" style={{ ...INPUT_STYLE, maxWidth: '280px' }} />
              </FormRow>

              <FormRow label="VAT / BTW" hint="For non-IN (e.g. NL) invoices">
                <input type="text" value={form.vatBtw || ''} onChange={e => setPrimary('vatBtw', e.target.value.toUpperCase())} placeholder="e.g. NL123456789B01" style={{ ...INPUT_STYLE, maxWidth: '300px' }} />
              </FormRow>

              <FormRow label="Payment Terms" hint="Days until payment is due after invoice date">
                <div className="brio-flex brio-items-center brio-gap-xs">
                  <input type="number" min="0" max="365" step="1" value={form.paymentTermsDays || ''} onChange={e => setPrimary('paymentTermsDays', e.target.value)} placeholder="e.g. 30" style={{ ...INPUT_STYLE, maxWidth: '100px' }} />
                  <span className="brio-text-xs brio-text-grey">days</span>
                  {!form.paymentTermsDays && form.invoiceType && (
                    <span className="brio-text-xs" style={{ color: 'var(--brio-red)', marginLeft: '8px' }}>Required for invoicing</span>
                  )}
                </div>
              </FormRow>

              {/* Default Tax Regime */}
              <FormRow label="Default Tax Regime" hint="Pre-fills the tax regime, GST % and office (letterhead) on new invoices for this client.">
                <select
                  value={form.defaultTaxRegime || ''}
                  onChange={e => setForm({ ...form, defaultTaxRegime: e.target.value })}
                  style={{ ...SELECT_STYLE, width: 'auto', maxWidth: '260px' }}
                >
                  <option value="">— not set —</option>
                  {TAX_REGIME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </FormRow>

              {/* Requires Proforma Invoice */}
              <FormRow label="Requires Proforma Invoice" hint="Enable the proforma invoice date column on the Timeline page.">
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={form.requiresProformaInvoice === true}
                    onChange={e => setForm({ ...form, requiresProformaInvoice: e.target.checked ? true : null })}
                    style={{ accentColor: 'var(--brio-gold, #9F902A)' }}
                  />
                  <span style={{ fontSize: '11px' }}>Enable proforma invoicing workflow</span>
                </label>
              </FormRow>

              {/* Planned Date Threshold */}
              <FormRow label="Planned Date Threshold" hint="Min days of change to trigger a planned date revised email. Default: 7 days.">
                <input
                  type="number"
                  min="1"
                  max="90"
                  placeholder="7"
                  value={form.plannedDateTriggerThresholdDays ?? ''}
                  onChange={e => setForm({ ...form, plannedDateTriggerThresholdDays: e.target.value })}
                  style={{ ...INPUT_STYLE, width: '80px', fontSize: '11px' }}
                />
              </FormRow>

              {/* ── Contact Preferences (per-event email overrides) ── */}
              {(() => {
                const allEmails = [];
                const priEmail = form.clientPrimaryEmail || '';
                if (priEmail) allEmails.push({ email: priEmail, name: form.clientName || '', label: 'Primary' });
                (form.additionalContacts || []).forEach(c => {
                  const em = c.primaryEmail || c.email || '';
                  if (em) allEmails.push({ email: em, name: c.name || '', label: c.relationship || c.company || '' });
                });
                if (allEmails.length === 0) return null;

                const prefs = form.contactPreferences || {};
                const eventTypes = [
                  { key: 'invoice_sent', label: 'Invoicing' },
                  { key: 'stage_approved', label: 'Approvals' },
                  { key: 'stage_initiated', label: 'Stage updates' },
                ];
                const toggleEmail = (eventKey, email) => {
                  const current = prefs[eventKey] || [];
                  const next = current.includes(email)
                    ? current.filter(e => e !== email)
                    : [...current, email];
                  setForm(prev => ({ ...prev, contactPreferences: { ...prefs, [eventKey]: next } }));
                };
                return (
                  <>
                    <FormRow sectionHeader label="Contact Preferences" />
                    <tr>
                      <td colSpan="2" style={{ padding: '4px 12px 2px' }}>
                        <div style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)' }}>
                          Which contacts receive emails for each timeline event type.
                          Leave empty to use the primary contact as default.
                        </div>
                      </td>
                    </tr>
                    {eventTypes.map(evt => (
                      <FormRow key={evt.key} label={evt.label}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {allEmails.map((c, i) => (
                            <label key={i} style={{
                              display: 'flex', alignItems: 'center', gap: '6px',
                              fontSize: '12px', cursor: 'pointer', padding: '2px 0',
                            }}>
                              <input
                                type="checkbox"
                                checked={(prefs[evt.key] || []).includes(c.email)}
                                onChange={() => toggleEmail(evt.key, c.email)}
                                style={{ accentColor: 'var(--brio-gold, #9F902A)', width: '13px', height: '13px' }}
                              />
                              <span>{c.name || c.email}</span>
                              {c.name && <span style={{ color: 'var(--brio-muted, #94a3b8)', fontSize: '11px' }}>{c.email}</span>}
                              {c.label && <span style={{ color: 'var(--brio-muted, #94a3b8)', fontSize: '10px' }}>({c.label})</span>}
                            </label>
                          ))}
                        </div>
                      </FormRow>
                    ))}
                  </>
                );
              })()}

            </tbody>
          </BrioTable>
        </BrioByCode>
      )}

      {/* ═══════════════════════════════════════════════════
          PEOPLE TAB
         ═══════════════════════════════════════════════════ */}
      {activeTab === 'people' && (
        <div>
          {/* Toolbar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '6px 0 8px',
          }}>
            <button
              type="button"
              onClick={collapseAll}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '11px', fontWeight: 500, color: 'var(--brio-muted, #94a3b8)',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
              }}
            >
              <ChevronUp size={12} /> Collapse all
            </button>
            <button
              type="button"
              onClick={addContact}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                fontSize: '11px', fontWeight: 500, color: 'var(--brio-gold, #9F902A)',
              }}
            >
              <UserPlus size={13} /> Add contact
            </button>
          </div>

          <BrioByCode code={MODAL_TABLE_CODE}>
            <BrioTable {...modalTableProps}>
              <tbody>

                {/* ── Primary Contact Drawer ──────────────── */}
                {renderDrawerHeader('primary', form.clientName || 'Primary Contact', (
                  <span style={PRIMARY_BADGE_FIXED}>Primary</span>
                ))}
                {isDrawerOpen('primary') && (
                  <ContactFieldsSection
                    data={primaryData}
                    onChange={onPrimaryChange}
                    showRelationship={true}
                    hideIdentity
                    expandMode={getExpandMode('primary')}
                    onExpandMode={(mode) => setExpandMode('primary', mode)}
                  />
                )}

                {/* ── Additional Contacts ─────────────────── */}
                {contacts.map((contact, idx) => {
                  const contactKey = `contact-${idx}`;
                  const contactLabel = contact.name || `Contact ${idx + 2}`;
                  const relLabel = contact.relationship === 'other'
                    ? (contact.relationshipOther || 'Other')
                    : RELATIONSHIP_TYPES.find(r => r.value === contact.relationship)?.label || '';
                  const open = isDrawerOpen(contactKey);

                  return (
                    <React.Fragment key={idx}>
                      {/* Drawer header */}
                      <tr>
                        <td colSpan="2" style={{
                          padding: '6px 12px',
                          borderBottom: '1px solid var(--brio-border, #e2e8f0)',
                          background: '#fafbfc',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                          onClick={() => toggleDrawer(contactKey)}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: 500 }}>
                              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                              <span>{contactLabel}</span>
                              {relLabel && <span style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)', fontWeight: 400 }}>({relLabel})</span>}
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); togglePrimary(idx); }}
                                style={contact.isPrimary ? PRIMARY_BADGE_ON : PRIMARY_BADGE_OFF}
                              >
                                Primary
                              </button>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); removeContact(idx); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                              title="Remove contact"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Drawer body */}
                      {open && (
                        <ContactFieldsSection
                          data={contact}
                          onChange={(updated) => updateContact(idx, updated)}
                          showRelationship={true}
                          hideIdentity={false}
                          expandMode={getExpandMode(contactKey)}
                          onExpandMode={(mode) => setExpandMode(contactKey, mode)}
                        />
                      )}
                    </React.Fragment>
                  );
                })}

                {contacts.length === 0 && (
                  <tr>
                    <td colSpan="2" style={{ padding: '8px 12px', fontSize: '12px', color: 'var(--brio-muted, #94a3b8)', fontStyle: 'italic' }}>
                      No additional contacts. Click "Add contact" above.
                    </td>
                  </tr>
                )}

              </tbody>
            </BrioTable>
          </BrioByCode>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════
          NOTES TAB
         ═══════════════════════════════════════════════════ */}
      {activeTab === 'notes' && (
        <div style={{ padding: '12px 0' }}>
          <label style={{
            display: 'block', fontSize: '13px', fontWeight: 500,
            color: 'var(--brio-text, #475569)', marginBottom: '6px',
          }}>
            Particularities & General Notes
          </label>
          <p style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)', margin: '0 0 8px' }}>
            Noteworthy particularities for this client group in general.
          </p>
          <textarea
            value={form.likesDislikes || ''}
            onChange={e => setPrimary('likesDislikes', e.target.value)}
            placeholder="Important notes about this client group…"
            style={{ ...TEXTAREA_STYLE, minHeight: '180px' }}
            rows={10}
          />
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Helper: form state → DB payload (camelCase → snake_case)
// Note: custom_date_label, custom_date, dietary_other do NOT have
// their own DB columns. They are packed into the existing
// family_members JSONB column as a metadata entry (the Family
// Members UI section was removed, so the column is repurposed).
// ═══════════════════════════════════════════════════════════════════
function formToDbPayload(form) {
  // Pack primary-contact-only fields into family_members JSONB
  const primaryMeta = {
    _meta: true,
    relationship: (form.clientRelationship || '').trim(),
    relationshipOther: (form.clientRelationshipOther || '').trim(),
    company: (form.clientCompany || '').trim(),
    customDateLabel: (form.clientCustomDateLabel || '').trim(),
    customDate: form.clientCustomDate || '',
    dietaryOther: (form.clientDietaryOther || '').trim(),
  };
  const hasMeta = primaryMeta.relationship || primaryMeta.relationshipOther || primaryMeta.company || primaryMeta.customDateLabel || primaryMeta.customDate || primaryMeta.dietaryOther;

  return {
    name: (form.clientName || '').trim(),
    organisation: (form.clientOrganisation || '').trim(),
    linkedin_url: (form.clientLinkedinUrl || '').trim(),
    instagram_url: (form.clientInstagramUrl || '').trim(),
    address: (form.clientAddress || '').trim(),
    mobile: (form.clientMobile || '').trim(),
    whatsapp: (form.clientWhatsapp || '').trim(),
    custom_phone_label: (form.clientCustomPhoneLabel || '').trim(),
    custom_phone_number: (form.clientCustomPhoneNumber || '').trim(),
    primary_email: (form.clientPrimaryEmail || '').trim(),
    secondary_email: (form.clientSecondaryEmail || '').trim(),
    dob: form.clientDob || null,
    anniversary: form.clientAnniversary || null,
    dietary: form.clientDietary || null,
    additional_contacts: (form.additionalContacts || []).filter(c => c.name?.trim()),
    family_members: hasMeta ? [primaryMeta] : [],
    likes_dislikes: (form.likesDislikes || '').trim() || null,
    invoicing_name: (form.invoicingName || '').trim() || null,
    invoice_type: (form.invoiceType || '').trim() || null,
    invoice_address: (form.invoiceAddress || '').trim() || null,
    gst: (form.gst || '').trim() || null,
    pan: (form.pan || '').trim() || null,
    sac_code: (form.sacCode || '').trim() || null,
    place_of_supply: (form.placeOfSupply || '').trim() || null,
    vat_btw: (form.vatBtw || '').trim() || null,
    payment_terms_days: form.paymentTermsDays !== '' && form.paymentTermsDays != null ? Number(form.paymentTermsDays) : null,
    defaultTaxRegime: form.defaultTaxRegime || null,
    requiresProformaInvoice: form.requiresProformaInvoice,
    plannedDateTriggerThresholdDays: form.plannedDateTriggerThresholdDays !== '' ? parseInt(form.plannedDateTriggerThresholdDays) || null : null,
    contact_preferences: form.contactPreferences || {},
  };
}

// ═══════════════════════════════════════════════════════════════════
// Helper: DB record (mapped) → form state
// Extracts primary-contact metadata from the family_members JSONB
// ═══════════════════════════════════════════════════════════════════
function dbToFormState(client) {
  // Extract metadata packed into family_members
  const fam = Array.isArray(client.familyMembers) ? client.familyMembers : [];
  const meta = fam.find(m => m && m._meta) || {};

  return {
    editClientId: client.id,
    clientName: client.name || '',
    clientOrganisation: client.organisation || '',
    clientLinkedinUrl: client.linkedinUrl || '',
    clientInstagramUrl: client.instagramUrl || '',
    clientAddress: client.address || '',
    clientMobile: client.mobile || '',
    clientWhatsapp: client.whatsapp || '',
    clientCustomPhoneLabel: client.customPhoneLabel || '',
    clientCustomPhoneNumber: client.customPhoneNumber || '',
    clientPrimaryEmail: client.primaryEmail || client.email || '',
    clientSecondaryEmail: client.secondaryEmail || '',
    clientDob: client.dob || '',
    clientAnniversary: client.anniversary || '',
    clientRelationship: meta.relationship || '',
    clientRelationshipOther: meta.relationshipOther || '',
    clientCompany: meta.company || '',
    clientCustomDateLabel: meta.customDateLabel || '',
    clientCustomDate: meta.customDate || '',
    clientDietary: client.dietary || '',
    clientDietaryOther: meta.dietaryOther || '',
    additionalContacts: Array.isArray(client.additionalContacts) ? client.additionalContacts : [],
    likesDislikes: client.likesDislikes || '',
    invoicingName: client.invoicingName || '',
    invoiceType: client.invoiceType || '',
    invoiceAddress: client.invoiceAddress || '',
    gst: client.gst || '',
    pan: client.pan || '',
    sacCode: client.sacCode || '',
    placeOfSupply: client.placeOfSupply || '',
    vatBtw: client.vatBtw || '',
    paymentTermsDays: client.paymentTermsDays != null ? String(client.paymentTermsDays) : '',
    defaultTaxRegime: normalizeTaxRegime(client.defaultTaxRegime) || '',
    requiresProformaInvoice: client.requiresProformaInvoice ?? null,
    plannedDateTriggerThresholdDays: client.plannedDateTriggerThresholdDays ?? '',
    contactPreferences: client.contactPreferences || {},
  };
}


// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
export default function ClientsPage({
  // Data
  activeClients,
  currentUser,
  // State (from App.js)
  setModal,
  // Supabase
  useSupabaseDB,
  supabaseClients,
  clients,
  setClients,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'admin-pa';

  // (#2) Filter out inquiry-created clients that haven't been converted yet.
  const filteredActiveClients = useMemo(() => {
    return (activeClients || [])
      .filter(c => c.origin !== 'inquiry' || c.isConverted === true)
      .sort((a, b) => {
        const nameA = (a.organisation || a.name || '').toLowerCase();
        const nameB = (b.organisation || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
  }, [activeClients]);

  // ── Local modal state ─────────────────────────────────────────────
  const [localModal, setLocalModal] = useState(null);  // 'addClient' | 'clientDetail' | 'confirmDeleteClient'
  const [localForm, setLocalForm] = useState(blankClientForm());
  const [saving, setSaving] = useState(false);

  // ── Open Add ──────────────────────────────────────────────────────
  const openAddModal = () => {
    setLocalForm(blankClientForm());
    setLocalModal('addClient');
  };

  // ── Open Client Detail View ─────────────────────────────────────
  const openDetailView = (client) => {
    setLocalForm(dbToFormState(client));
    setLocalModal('clientDetail');
  };

  // ── Save Add ──────────────────────────────────────────────────────
  const handleAddClient = async () => {
    if (!localForm.clientName?.trim() && !localForm.clientOrganisation?.trim()) {
      alert('Enter a client name or organisation'); return;
    }
    setSaving(true);
    try {
      const payload = formToDbPayload(localForm);
      if (useSupabaseDB) {
        const result = await supabaseClients.createClient(payload);
        if (!result.success) { alert('Error creating client: ' + result.error); return; }
      } else {
        setClients(prev => [...prev, { id: Date.now(), ...payload, email: payload.primary_email }]);
      }
      setLocalModal(null);
      setLocalForm(blankClientForm());
    } catch (err) {
      alert('Error: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  // ── Save Edit ─────────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!localForm.clientName?.trim() && !localForm.clientOrganisation?.trim()) {
      alert('Enter a client name or organisation'); return;
    }
    setSaving(true);
    try {
      const payload = formToDbPayload(localForm);
      if (useSupabaseDB) {
        const result = await supabaseClients.updateClient(localForm.editClientId, payload);
        if (!result.success) { alert('Error updating client: ' + result.error); return; }
      } else {
        setClients(prev => prev.map(c => c.id === localForm.editClientId ? { ...c, ...payload, email: payload.primary_email } : c));
      }
      setLocalModal(null);
      setLocalForm(blankClientForm());
    } catch (err) {
      alert('Error: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────
  const handleDeleteClient = async () => {
    setSaving(true);
    try {
      if (useSupabaseDB) {
        const result = await supabaseClients.deleteClient(localForm.editClientId);
        if (!result.success) { alert('Error deleting client: ' + result.error); return; }
      } else {
        setClients(prev => prev.filter(c => c.id !== localForm.editClientId));
      }
      setLocalModal(null);
      setLocalForm(blankClientForm());
    } catch (err) {
      alert('Error: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  };

  // ── Display helpers ───────────────────────────────────────────────
  const displayName = (c) => c.organisation || c.name || '';
  const displayEmail = (c) => c.primaryEmail || c.email || '';
  const displayPhone = (c) => c.mobile || c.whatsapp || '';

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font">
      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-page-header__left">

            </div>
            {isAdmin && (
              <div className="brio-page-header__right">
                <button onClick={openAddModal} className="brio-btn brio-btn-primary">
                  <Plus className="w-4 h-4" />Add Client
                </button>
              </div>
            )}
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          {filteredActiveClients.length > 0 ? (
            <BrioAnalyticsSurface padding="m">
              <BrioByCode code={PRESENTATION_CODE}>
                <BrioTable {...tableProps}>
                  <thead>
                    <tr>
                      <th>name</th>
                      <th>email</th>
                      <th style={{ whiteSpace: 'nowrap' }}>phone</th>
                      {isAdmin && <th className="text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredActiveClients.map(c => (
                      <tr key={c.id}>
                        <td>
                          <span className="brio-font-medium">{displayName(c)}</span>
                          {c.organisation && c.name && (
                            <div style={{ fontSize: '11px', color: 'var(--brio-muted, #94a3b8)', marginTop: '1px' }}>
                              {c.name}
                            </div>
                          )}
                        </td>
                        <td><span className="brio-text-grey">{displayEmail(c)}</span></td>
                        <td style={{ whiteSpace: 'nowrap' }}><span className="brio-text-grey">{displayPhone(c)}</span></td>
                        {isAdmin && (
                          <td className="text-center" style={{ width: '1%', whiteSpace: 'nowrap' }}>
                            <BrioActionGroup center>
                              <BrioIconButton
                                icon="edit"
                                variant="edit"
                                title="View client details"
                                onClick={() => openDetailView(c)}
                              />
                            </BrioActionGroup>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </BrioTable>
              </BrioByCode>
            </BrioAnalyticsSurface>
          ) : (
            <BrioAnalyticsSurface padding="m">
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '48px 0' }}>
                No clients added yet
              </div>
            </BrioAnalyticsSurface>
          )}

          {!isAdmin && (
            <p className="brio-analytics-muted brio-mt-s">Contact admin to add new clients.</p>
          )}
        </div>
      </BrioFrame>

      {/* ════════════════════════════════════════════════════════════
          ADD CLIENT — Client Detail View (create mode)
         ════════════════════════════════════════════════════════════ */}
      {localModal === 'addClient' && (
        <BrioModal
          title="Add Client"
          onClose={() => { setLocalModal(null); setLocalForm(blankClientForm()); }}
          width="lg"
          footer={
            <BrioActionGroup align="end">
              <button onClick={() => { setLocalModal(null); setLocalForm(blankClientForm()); }} className="brio-btn brio-btn-secondary" disabled={saving}>Cancel</button>
              <button
                onClick={handleAddClient}
                className="brio-btn brio-btn-primary"
                disabled={saving || (!localForm.clientName?.trim() && !localForm.clientOrganisation?.trim())}
              >
                {saving ? 'Creating…' : 'Create Client'}
              </button>
            </BrioActionGroup>
          }
        >
          <ClientDetailViewBody form={localForm} setForm={setLocalForm} />
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════
          CLIENT DETAIL VIEW — edit mode
         ════════════════════════════════════════════════════════════ */}
      {localModal === 'clientDetail' && (
        <BrioModal
          title="Client Details"
          onClose={() => { setLocalModal(null); setLocalForm(blankClientForm()); }}
          width="lg"
          footer={
            <BrioActionGroup>
              <button onClick={() => setLocalModal('confirmDeleteClient')} className="brio-btn brio-btn-danger" disabled={saving}>Delete Client</button>
              <button onClick={() => { setLocalModal(null); setLocalForm(blankClientForm()); }} className="brio-btn brio-btn-secondary" disabled={saving}>Cancel</button>
              <button
                onClick={handleSaveEdit}
                className="brio-btn brio-btn-primary"
                disabled={saving || (!localForm.clientName?.trim() && !localForm.clientOrganisation?.trim())}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </button>
            </BrioActionGroup>
          }
        >
          <ClientDetailViewBody form={localForm} setForm={setLocalForm} />
        </BrioModal>
      )}

      {/* ════════════════════════════════════════════════════════════
          CONFIRM DELETE MODAL
         ════════════════════════════════════════════════════════════ */}
      {localModal === 'confirmDeleteClient' && (
        <BrioModal
          title="Delete Client"
          titleDanger
          onClose={() => setLocalModal('clientDetail')}
          width="sm"
          footer={
            <BrioActionGroup>
              <button onClick={() => setLocalModal('clientDetail')} className="brio-btn brio-btn-secondary" disabled={saving} style={{ flex: 1 }}>Cancel</button>
              <button onClick={handleDeleteClient} className="brio-btn brio-btn-danger" disabled={saving} style={{ flex: 1 }}>
                {saving ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </BrioActionGroup>
          }
        >
          <div style={{ padding: '8px 0', fontSize: '14px', color: 'var(--brio-text, #1e293b)' }}>
            <p>Are you sure you want to delete <strong>{localForm.clientOrganisation || localForm.clientName}</strong>?</p>
            <p style={{ marginTop: '8px', fontSize: '12px', color: 'var(--brio-muted, #94a3b8)' }}>
              This action cannot be undone. Projects linked to this client will remain but lose the client reference.
            </p>
          </div>
        </BrioModal>
      )}
    </div>
  );
}
